/**
 * PINE → D:\pine\frontend\src\components\Modals\workstation\GeneratedDocumentsModal.jsx
 *
 * Lists every document generated for this role: status-update decks, candidate dossiers and
 * candidate profiles. Shows date (IST), doc type, generated by, statuses covered, candidates,
 * status and a download for anything finished.
 */

import React, { useEffect, useState } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import toastr from "../../../utils/toast";

const STATUS_BADGE = {
  estimated: "bg-secondary",
  queued: "bg-secondary",
  running: "bg-info",
  done: "bg-success",
  failed: "bg-danger",
};

export default function GeneratedDocumentsModal({ role_id, token, onClose }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/workstation/generated_documents/${role_id}`,
        auth,
      );
      setDocs(res.data?.documents || []);
    } catch (err) {
      console.error(err);
      toastr.error("Could not load generated documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [role_id]);

  const handleDownload = async (doc) => {
    try {
      // decks download by job; dossiers and profiles are files in the candidate media folders
      const res =
        doc.doc_type === "Deck"
          ? await axios.get(
              `${API_BASE_URL}/workstation/status_update_download/${doc.job_id}`,
              { ...auth, responseType: "blob" },
            )
          : await axios.get(
              `${process.env.REACT_APP_BASE_URL_BACK}files/candidates/media/${doc.folder}/${doc.file_name}`,
              { ...auth, responseType: "blob" },
            );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.file_name || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toastr.error("Download failed");
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Generated Documents</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <CircularProgress size={24} />
              </div>
            ) : docs.length === 0 ? (
              <p className="text-muted mb-0">
                No documents generated for this role yet.
              </p>
            ) : (
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Doc type</th>
                    <th>Generated by</th>
                    <th>Statuses covered</th>
                    <th>Candidates</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.key}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {d.date ? d.date.split("-").reverse().join("/") : "—"}
                      </td>
                      <td>{d.doc_type}</td>
                      <td>{d.generated_by || "—"}</td>
                      <td>
                        <span title={(d.statuses_covered || []).join(", ")}>
                          {d.statuses_covered?.length
                            ? `${d.statuses_covered.length} status${d.statuses_covered.length > 1 ? "es" : ""}`
                            : "—"}
                        </span>
                      </td>
                      <td>{d.candidates ?? "—"}</td>
                      <td>
                        <span
                          className={`badge ${STATUS_BADGE[d.status] || "bg-secondary"}`}
                        >
                          {d.status}
                        </span>
                        {d.status === "failed" && (
                          <span
                            className="text-danger ms-1"
                            style={{ fontSize: 11 }}
                          >
                            failed
                          </span>
                        )}
                      </td>
                      <td className="text-end">
                        {d.downloadable && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleDownload(d)}
                          >
                            <i className="fa-solid fa-download" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-sm btn-outline-secondary" onClick={load}>
              Refresh
            </button>
            <button className="btn btn-sm btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
